import {
    Box,
    List,
    ListItem,
    Checkbox,
    Input,
    InputGroup,
    InputLeftElement,
    Text,
    VStack,
    Badge,
    Flex,
    Icon,
    Tooltip,
    Button,
    useColorMode
} from '@chakra-ui/react';
import { MdSearch, MdInfoOutline } from 'react-icons/md';
import { useState, useMemo } from 'react';
import { useAssistant } from "../../context/AssistantContext";

const FileSelect = () => {
    const { formState, knowledgeBaseFiles, setFormState } = useAssistant();
    const [searchQuery, setSearchQuery] = useState('');
    const { colorMode } = useColorMode();

    const handleFileSelect = (fileId) => {
        setFormState(prev => {
            const prevKnowledgebases = Array.isArray(prev.knowledgebases) ? prev.knowledgebases : [];
            const fileExists = prevKnowledgebases.some(kb => kb._id === fileId);

            if (fileExists) {
                // Remove the file if it exists
                return {
                    ...prev,
                    knowledgebases: prevKnowledgebases.filter(kb => kb._id !== fileId)
                };
            } else {
                // Add the file if it doesn't exist
                const fileToAdd = knowledgeBaseFiles.find(f => f._id === fileId);
                return {
                    ...prev,
                    knowledgebases: [...prevKnowledgebases, fileToAdd]
                };
            }
        });
    };

    const handleSelectAll = () => {
        setFormState(prevState => ({ ...prevState, knowledgebases: knowledgeBaseFiles }));
    };

    const handleClearAll = () => {
        setFormState(prevState => ({ ...prevState, knowledgebases: [] }));
    };

    const filteredFiles = useMemo(() => {
        if (!searchQuery) return knowledgeBaseFiles;
        return knowledgeBaseFiles.filter(file =>
            file.fileName.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [knowledgeBaseFiles, searchQuery]);

    return (
        <VStack spacing={4} align="stretch">
            <InputGroup>
                <InputLeftElement pointerEvents="none">
                    <Icon as={MdSearch} color={colorMode === 'light' ? 'gray.400' : 'gray.500'} />
                </InputLeftElement>
                <Input
                    placeholder="Search files..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    bg={colorMode === 'light' ? 'white' : 'gray.700'}
                    borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                    _hover={{
                        borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500'
                    }}
                />
            </InputGroup>

            <Flex justify="space-between" mb={2}>
                <Button size="sm" onClick={() => handleSelectAll()}>Select All</Button>
                <Button size="sm" onClick={() => handleClearAll()}>Clear All</Button>
            </Flex>

            <Box
                maxH="300px"
                overflowY="auto"
                p={2}
                borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                borderWidth={1}
                borderRadius="md"
                bg={colorMode === 'light' ? 'white' : 'gray.700'}
            >
                {filteredFiles.length === 0 ? (
                    <Text
                        color={colorMode === 'light' ? 'gray.500' : 'gray.400'}
                        textAlign="center"
                        py={4}
                    >
                        {searchQuery ? 'No files match your search' : 'No files available'}
                    </Text>
                ) : (
                    <List spacing={3}>
                        {filteredFiles.map((file) => {
                            const isSelected = formState?.knowledgebases?.some((kb) => {

                                return kb._id === file._id;
                            });

                            return (
                                <ListItem key={file._id}>
                                    <Flex align="center" justify="space-between">
                                        <Checkbox
                                            isChecked={isSelected}
                                            onChange={() => handleFileSelect(file._id)}
                                            color={colorMode === 'light' ? 'gray.800' : 'white'}
                                            maxW="80%"
                                        >
                                            <Text
                                                noOfLines={1}
                                                color={colorMode === 'light' ? 'gray.700' : 'gray.300'}
                                            >
                                                {file.fileName}
                                            </Text>
                                        </Checkbox>
                                        <Tooltip label="File information">
                                            <Badge
                                                colorScheme={isSelected ? 'green' : 'gray'}
                                                variant="subtle"
                                                fontSize="xs"
                                            >
                                                {new Date(file.createdAt).toLocaleDateString()}
                                            </Badge>
                                        </Tooltip>
                                    </Flex>
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </Box>

            <Text
                fontSize="sm"
                color={colorMode === 'light' ? 'gray.500' : 'gray.400'}
                textAlign="right"
            >
                {formState?.knowledgebases?.length || 0} of {knowledgeBaseFiles.length} files selected
            </Text>
        </VStack>
    );
};

export default FileSelect;